import InvalidCoordinates from "./errors/InvalidCoordinates"

/*
 * The view of the current state of the game
 * in the view part of the browser screen.
 */
class Viewport {
  constructor({ width, height, log, element }) {
    this._setDimensions({ width, height })
    this.log = log.forClass("Viewport")
    this.CSS_CLASS_PROPERTY = "css_class"
    this.element = element
    if (!this.element) {
      throw `[Viewport#constructor] element is required`
    }

  }

  isTooSmall() {
    return (this.width < 10) || (this.height < 7)
  }

  hide() {
    this.element.style["display"] = "none"
  }
  show({width,height,tileWidth,tileHeight}) {
    this._setDimensions({ width, height })

    const widthInPixels  = width  * tileWidth
    const heightInPixels = height * tileHeight

    this.element.style["display"]   = "block"
    this.element.style["width"]     = `${widthInPixels}px`
    this.element.style["min-width"] = `${widthInPixels}px`
    this.element.style["height"]    = `${heightInPixels}px`
  }
  clear() {
    this.element.textContent = ""
  }
  addRow(gameMapRow) {
    this.element.appendChild(gameMapRow)
  }

  render(game) {
    if (!game) {
      throw "[Viewport#render] Game is required"
    }
    const logger = this.log.measurePerformance("render")
    if (this.element.style["display"] === "none") {
      logger.end({ hidden: true })
      return
    }

    const mapOrigin = this._mapOrigin(game)

    this._renderTerrain(mapOrigin, game)
    this._renderPortals(mapOrigin, game)
    this._renderMessages(mapOrigin, game)
    this._renderCharacters(mapOrigin, game)
    this._renderTraveler(mapOrigin, game)
    this._blockSightLines(mapOrigin, game)

    logger.end()
  }

  _blockSightLines(mapOrigin, game) {
    const travelerViewMax = game.gameMap.traveler.view_max
    this._eachViewportTile(mapOrigin, game.gameMap, ({ mapX, mapY, cssModifier, $div}) => {

      let xTowardTraveler = 0
      let yTowardTraveler = 0

      if      ( mapX > game.travelerX) { xTowardTraveler = -1; }
      else if ( mapX < game.travelerX) { xTowardTraveler = 1;  }

      if      ( mapY > game.travelerY) { yTowardTraveler = -1; }
      else if ( mapY < game.travelerY) { yTowardTraveler = 1;  }

      let x = mapX;
      let y = mapY;
      let visible = 1;

      while ( !( (x == game.travelerX) && (y == game.travelerY) )) {

        x = x + xTowardTraveler;
        y = y + yTowardTraveler;

        if (x == game.travelerX) { xTowardTraveler = 0; }
        if (y == game.travelerY) { yTowardTraveler = 0; }

        
        if ((xTowardTraveler == 0) && (yTowardTraveler == 0)) {
          break;
        }

        const tile = game.gameMap.terrainTileAt(x,y)

        if (tile.height >= 0) {
          let diff = tile.height - travelerViewMax
          if (diff > 0) {
            visible = visible - diff;
            if (visible < 0) {
              break;
            }
          }
        }


      }
      if (visible < 0) {
        $div.className = "tile tile-blank"
      }
    })
  }

  _renderTraveler(mapOrigin, game) {
    this._eachViewportTile(mapOrigin, game.gameMap, ({ mapX, mapY, cssModifier, $div}) => {
      if ((game.travelerX == mapX) && (game.travelerY == mapY)) {
        const traveler = game.gameMap.travelerTile()
        $div.className = `tile ${traveler[this.CSS_CLASS_PROPERTY]}${cssModifier}`
        return "stop"
      }
    })
  }

  _renderTerrain(mapOrigin, game) {
    const logger = this.log.measurePerformance("_renderTerrain")
    this._eachViewportTile(mapOrigin, game.gameMap, ({ mapX, mapY, cssModifier, $div}) => {
      const tile = game.gameMap.terrainTileAt(mapX,mapY)
      $div.className = `tile ${tile[this.CSS_CLASS_PROPERTY]}${cssModifier}`
    })
    logger.end()
  }

  _renderPortals(mapOrigin, game) {
    this._eachViewportTile(mapOrigin, game.gameMap, ({ mapX, mapY, cssModifier, $div}) => {
      const tile = game.gameMap.portalAt(mapX,mapY)
      if (tile) {
        $div.className = `tile ${tile[this.CSS_CLASS_PROPERTY]}${cssModifier}`
      }
    })
  }

  _renderMessages(mapOrigin, game) {
    this._eachViewportTile(mapOrigin, game.gameMap, ({ mapX, mapY, cssModifier, $div}) => {
      const tile = game.gameMap.messageAt(mapX,mapY)
      if (tile) {
        $div.className = `tile ${tile[this.CSS_CLASS_PROPERTY]}${cssModifier}`
      }
    })
  }

  _renderCharacters(mapOrigin, game) {
    this._eachViewportTile(mapOrigin, game.gameMap, ({ mapX, mapY, cssModifier, $div}) => {
      const tile = game.gameMap.characterAt(mapX,mapY)
      if (tile) {
        $div.className = `tile ${tile[this.CSS_CLASS_PROPERTY]}${cssModifier}`
      }
    })
  }

  /** Iterates over each tile on the map that is
   * shown in the viewport.
   *
   * mapOrigin: origin {x,y} for where to start rendering
   * map: the GameMap
   * f: callback called on each tile: return "stop" to stop iteration
   */
  _eachViewportTile(mapOrigin, gameMap, f) {
    const $rowsInViewport = this.element.getElementsByClassName("row")

    let viewportY = 0
    while (viewportY < this.height) {

      const $thisRowInViewport = $rowsInViewport[viewportY]

      let viewportX = 0
      while (viewportX < this.width) {

        const [mapX,mapY] = this._translateFromOrigin(gameMap, mapOrigin,viewportX,viewportY)

        const $tileInViewport = $thisRowInViewport.getElementsByClassName("tile")[viewportX]

        const colorPartition = gameMap.colorPartition(mapX,mapY)
        let cssModifier = ""
        if (colorPartition != 0) {
          cssModifier = "-" + colorPartition
        }

        const instructions = f({
          mapX: mapX,
          mapY: mapY,
          cssModifier: cssModifier,
          $div: $tileInViewport
        })

        if (instructions == "stop") {
          return
        }

        viewportX = viewportX + 1
      }

      viewportY = viewportY + 1
    }
  }

  _translateFromOrigin(gameMap, mapOrigin, viewportX, viewportY) {
    let mapX = mapOrigin.x + viewportX
    let mapY = mapOrigin.y + viewportY

    if (mapX >= gameMap.width) {
      mapX = mapX - gameMap.width
    }

    if (mapY >= gameMap.height) {
      mapY = mapY - gameMap.height
    }

    return [ mapX, mapY ]
  }

  /* Figure out subimage of the map to render */
  _mapOrigin(game) {

    const logger = this.log.method("_mapOrigin")

    const xAdjust = Math.floor(this.width / 2)
    const yAdjust = Math.floor(this.height / 2)

    let x = game.travelerX - xAdjust
    let y = game.travelerY - yAdjust

    logger.addDetails({ unadjustedOrigin: [ x, y ]})

    if (game.gameMap.flat) {
      logger.addDetails({ flat: true })
      if (x < 0) {
        x = 0
      }
      if (y < 0) {
        y = 0
      }
      logger.addDetails({
        adjustedOrigin: [ x + this.width, y + this.height ],
        mapDimensions: [ game.gameMap.width, game.gameMap.height ],
      })
      if ( (x + this.width) >= game.gameMap.width) {
        x = Math.max(game.gameMap.width - this.width,0)
      }
      if ( (y + this.height) >= game.gameMap.height) {
        y = Math.max(game.gameMap.height - this.height,0)
      }
    }
    else {
      logger.addDetails({ flat: false })
      if (x < 0) {
        x = game.gameMap.width + x
      }
      else if (x >= game.gameMap.width) {
        x = (game.gameMap.width - x)
      }
      if (y < 0) {
        y = game.gameMap.height + y
      }
      else if (y >= game.gameMap.height) {
        y = (game.gameMap.height - y)
      }
    }
    if ( (x < 0) || (y < 0) || (x >= game.gameMap.width) || (y >= game.gameMap.height) ) {
      throw new InvalidCoordinates({
        travelerX: game.travelerX,
        travelerY: game.travelerY,
        mapName: game.gameMap.name,
        computedX: x,
        computedY: y,
      })
    }
    logger.end({ finalOrigin: [ x, y ] })
    return { x: x, y: y }
  }
  _setDimensions({width,height}) {

    this.width = width
    this.height = height
    // We need an odd number of tiles each way so that it's easy to locate the center
    if (this.width % 2 === 0) {
      this.width = this.width - 1
    }

    if (this.height % 2 === 0) {
      this.height = this.height - 1
    }
  }
}

export default Viewport
